import { useMemo, useState } from 'react'
import { Copy, Check, AlertCircle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useBulkImportMenu, type ScannedCategory } from '@/lib/queries/usePOS'

const CHATGPT_PROMPT = `You are a precise menu data extractor. I will give you the raw text of a restaurant menu (or a description of one). Extract every menu item with its category, and return it as JSON in EXACTLY this shape, with NO surrounding prose or code fences:

{
  "categories": [
    {
      "name": "string",
      "items": [
        {
          "name": "string",
          "description": "string or null",
          "price": number or null,
          "orderRole": "standalone" or "child_only",
          "extraAppliesToCategories": ["category name"],
          "extraAppliesToItems": ["item name"],
          "variants": [{ "name": "string", "priceDelta": number or null }],
          "modifiers": [{ "name": "string", "priceDelta": number or null }],
          "options": [{ "name": "string", "priceDelta": number or null }]
        }
      ]
    }
  ]
}

Rules:
- Copy item names exactly as written. Do not paraphrase or translate.
- If a description is not on the menu, use null. Do not invent one.
- price must be a plain number (e.g. 12.5), not a string with currency. Use null if missing.
- For meal deals, bundles, sizes, or selectable choices, include variants/modifiers/options when the menu lists them.
- Use variants for sizes/styles that change the parent item price. Use modifiers/options for included or add-on choices such as pizza choices in a meal deal.
- If an item is an add-on/extra/topping that cannot be ordered alone, set orderRole to "child_only".
- For child_only items, set extraAppliesToCategories when it applies to a whole category, for example ["Pizza"], and set extraAppliesToItems for exact parent item names, for example ["Spaghetti Carbonara"].
- For normal orderable items, set orderRole to "standalone" and omit the applies arrays.
- Group items under their section/category headings exactly as printed.
- If an item has no category heading on the menu, put it in a category named "Menu".
- Do not include any commentary, only the JSON object.

Here is the menu:
<<<PASTE_YOUR_MENU_HERE>>>
`

interface ParsedResult {
  ok: boolean
  data?: ScannedCategory[]
  error?: string
  preview?: { categoryCount: number; itemCount: number }
}

function parseMenuJson(input: string): ParsedResult {
  const trimmed = input.trim()
  if (!trimmed) return { ok: false, error: 'Paste the JSON returned by ChatGPT.' }

  // Be lenient: tolerate accidental ```json fences if the user copied them.
  const stripped = trimmed
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```$/i, '')
    .trim()
  const jsonText = extractFirstJsonObject(stripped) ?? stripped

  let raw: unknown
  try {
    raw = JSON.parse(jsonText)
  } catch (err) {
    try {
      raw = JSON.parse(repairUnescapedQuotesInJsonStrings(jsonText))
    } catch {
      return { ok: false, error: `Invalid JSON: ${(err as Error).message}` }
    }
  }

  const obj = raw as { categories?: unknown }
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.categories)) {
    return { ok: false, error: 'Expected a top-level "categories" array.' }
  }

  const categories: ScannedCategory[] = []
  let itemCount = 0
  for (let i = 0; i < obj.categories.length; i++) {
    const cat = obj.categories[i] as { name?: unknown; items?: unknown }
    if (!cat || typeof cat !== 'object') {
      return { ok: false, error: `categories[${i}] is not an object.` }
    }
    if (typeof cat.name !== 'string' || !cat.name.trim()) {
      return { ok: false, error: `categories[${i}].name must be a non-empty string.` }
    }
    if (!Array.isArray(cat.items)) {
      return { ok: false, error: `categories[${i}].items must be an array.` }
    }
    const items: ScannedCategory['items'] = []
    for (let j = 0; j < cat.items.length; j++) {
      const item = cat.items[j] as {
        name?: unknown;
        description?: unknown;
        price?: unknown;
        orderRole?: unknown;
        extraAppliesToCategories?: unknown;
        extraAppliesToItems?: unknown;
        variants?: unknown;
        modifiers?: unknown;
        options?: unknown;
      }
      if (!item || typeof item !== 'object') {
        return { ok: false, error: `categories[${i}].items[${j}] is not an object.` }
      }
      if (typeof item.name !== 'string' || !item.name.trim()) {
        return { ok: false, error: `categories[${i}].items[${j}].name must be a non-empty string.` }
      }
      const description =
        item.description == null
          ? null
          : typeof item.description === 'string'
          ? item.description
          : null
      let price: number | null = null
      if (item.price != null) {
        const n = typeof item.price === 'number' ? item.price : parseFloat(String(item.price))
        if (Number.isFinite(n)) price = n
      }
      items.push({
        name: item.name.trim(),
        description,
        price,
        orderRole: item.orderRole === 'child_only' ? 'child_only' : 'standalone',
        extraAppliesToCategories: parseStringList(item.extraAppliesToCategories),
        extraAppliesToItems: parseStringList(item.extraAppliesToItems),
        variants: parseOptionList(item.variants),
        modifiers: parseOptionList(item.modifiers),
        options: parseOptionList(item.options),
      })
      itemCount++
    }
    categories.push({ name: cat.name.trim(), items })
  }

  if (categories.length === 0) {
    return { ok: false, error: 'No categories found in the JSON.' }
  }

  return {
    ok: true,
    data: categories,
    preview: { categoryCount: categories.length, itemCount },
  }
}

function parseStringList(input: unknown): string[] | undefined {
  if (!Array.isArray(input)) return undefined
  const values = input.map((value) => String(value ?? '').trim()).filter(Boolean)
  return values.length > 0 ? values : undefined
}

function extractFirstJsonObject(input: string) {
  const start = input.indexOf('{')
  if (start < 0) return null

  let depth = 0
  let inString = false
  let escaped = false

  for (let i = start; i < input.length; i++) {
    const char = input[i]

    if (inString) {
      if (escaped) {
        escaped = false
      } else if (char === '\\') {
        escaped = true
      } else if (char === '"') {
        const rest = input.slice(i + 1)
        const nextNonSpace = rest.match(/\S/)?.[0]
        if (!nextNonSpace || [',', '}', ']', ':'].includes(nextNonSpace)) {
          inString = false
        }
      }
      continue
    }

    if (char === '"') {
      inString = true
      continue
    }

    if (char === '{') depth++
    if (char === '}') {
      depth--
      if (depth === 0) return input.slice(start, i + 1)
    }
  }

  return null
}

function repairUnescapedQuotesInJsonStrings(input: string) {
  let output = ''
  let inString = false
  let escaped = false

  for (let i = 0; i < input.length; i++) {
    const char = input[i]

    if (!inString) {
      output += char
      if (char === '"') inString = true
      continue
    }

    if (escaped) {
      output += char
      escaped = false
      continue
    }

    if (char === '\\') {
      output += char
      escaped = true
      continue
    }

    if (char === '"') {
      const rest = input.slice(i + 1)
      const nextNonSpace = rest.match(/\S/)?.[0]
      if (nextNonSpace && ![',', '}', ']', ':'].includes(nextNonSpace)) {
        output += '\\"'
        continue
      }

      output += char
      inString = false
      continue
    }

    output += char
  }

  return output
}

function parseOptionList(input: unknown): ScannedCategory['items'][number]['modifiers'] {
  if (!Array.isArray(input)) return undefined

  const options = input
    .map((option) => {
      if (typeof option === 'string') {
        const name = option.trim()
        return name ? { name, priceDelta: 0 } : null
      }

      const raw = option as { name?: unknown; priceDelta?: unknown }
      if (!raw || typeof raw !== 'object' || typeof raw.name !== 'string' || !raw.name.trim()) {
        return null
      }

      const n = raw.priceDelta == null ? 0 : typeof raw.priceDelta === 'number' ? raw.priceDelta : parseFloat(String(raw.priceDelta))
      return {
        name: raw.name.trim(),
        priceDelta: Number.isFinite(n) ? n : 0,
      }
    })
    .filter(Boolean) as Array<{ name: string; priceDelta: number }>

  return options.length > 0 ? options : undefined
}

interface Props {
  open: boolean
  onClose: () => void
}

export function MenuJsonImportModal({ open, onClose }: Props) {
  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importedCount, setImportedCount] = useState<{ cats: number; items: number; variants: number; modifiers: number } | null>(null)
  const importMutation = useBulkImportMenu()

  const parsed = useMemo(() => parseMenuJson(text), [text])

  const reset = () => {
    setText('')
    setCopied(false)
    setImportError(null)
    setImportedCount(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(CHATGPT_PROMPT)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API can be unavailable on insecure origins. Fall back: select-all.
      const ta = document.createElement('textarea')
      ta.value = CHATGPT_PROMPT
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      } finally {
        document.body.removeChild(ta)
      }
    }
  }

  const handleImport = async () => {
    if (!parsed.ok || !parsed.data) return
    setImportError(null)
    try {
      const res = await importMutation.mutateAsync(parsed.data)
      const data = (res as any)?.data ?? {}
      setImportedCount({
        cats: Number(data.categoriesCreated ?? 0),
        items: Number(data.itemsCreated ?? 0),
        variants: Number(data.variantsCreated ?? 0),
        modifiers: Number(data.modifiersCreated ?? 0),
      })
      setText('')
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.')
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="Import menu from JSON" size="lg">
      <div className="space-y-4">
        {/* Step 1 — copy prompt */}
        <section className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Step 1 — Generate JSON with ChatGPT
              </h3>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Copy this prompt, paste it into ChatGPT (or any LLM), then append your menu where it says
                {' '}<code className="text-[var(--color-text-primary)]">&lt;&lt;&lt;PASTE_YOUR_MENU_HERE&gt;&gt;&gt;</code>.
                The model will return a JSON block ready for step 2.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={handleCopyPrompt}>
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy prompt
                </>
              )}
            </Button>
          </div>
          <pre className="text-[11px] leading-relaxed whitespace-pre-wrap text-[var(--color-text-muted)] max-h-32 overflow-y-auto bg-[var(--color-bg-base)] border border-[var(--color-border-subtle)] rounded-[var(--radius-sm)] p-2">
{CHATGPT_PROMPT}
          </pre>
        </section>

        {/* Step 2 — paste JSON */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              Step 2 — Paste the JSON ChatGPT returned
            </h3>
            {parsed.ok && parsed.preview && (
              <span className="text-xs text-[var(--color-text-muted)]">
                {parsed.preview.categoryCount} categories · {parsed.preview.itemCount} items
              </span>
            )}
          </div>
          <textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='{ "categories": [ { "name": "Starters", "items": [ ... ] } ] }'
            className="w-full px-3 py-2 text-xs font-mono rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-amber)] resize-y"
          />
          {text.trim() && !parsed.ok && (
            <div className="mt-2 flex items-start gap-2 text-xs text-[var(--color-accent-red)]">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{parsed.error}</span>
            </div>
          )}
        </section>

        {/* Result / errors */}
        {importError && (
          <div className="flex items-start gap-2 rounded-[var(--radius-md)] bg-red-500/10 border border-red-500/20 p-3 text-xs text-[var(--color-accent-red)]">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{importError}</span>
          </div>
        )}

        {importedCount && (
          <div className="rounded-[var(--radius-md)] bg-green-500/10 border border-green-500/20 p-3 text-xs text-green-400">
            Imported {importedCount.cats} new {importedCount.cats === 1 ? 'category' : 'categories'}, {importedCount.items}{' '}
            {importedCount.items === 1 ? 'item' : 'items'}, {importedCount.variants}{' '}
            {importedCount.variants === 1 ? 'variant' : 'variants'}, and {importedCount.modifiers}{' '}
            {importedCount.modifiers === 1 ? 'modifier/option' : 'modifiers/options'}. Existing entries were skipped.
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={handleClose}>
            {importedCount ? 'Done' : 'Cancel'}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!parsed.ok || importMutation.isPending}
            loading={importMutation.isPending}
            onClick={handleImport}
          >
            Import
          </Button>
        </div>
      </div>
    </Modal>
  )
}
